import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { OverlayScrollbars } from "overlayscrollbars";
import React from "react";
import ReactDOM from "react-dom/client";
import App from "./App";
import { installFocusModality } from "./utils/focusModality";

// Import global styles from design-variables if needed
import "@studio3t/generic-components/static-global-style.css";
// Geist Mono (400) for the code editor, per Figma.
import "@fontsource/geist-mono/400.css";
// Scrollbar styles for StyledTableScroll (overlayscrollbars) — the
// generic-components bundle strips its own CSS imports, so the app has
// to load this directly.
import "overlayscrollbars/overlayscrollbars.css";

// Single app-wide query cache. Conservative defaults that mirror the previous
// hand-rolled hooks: fetch on mount, no window-focus refetch (live data is kept
// fresh via per-query refetchInterval instead), one retry on transient errors.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 10_000,
    },
  },
});

installFocusModality(document);

// Scrollbars show only while the pointer is over the element that owns them
// and fade out after it leaves (TTTL-261), like native macOS scrollbars. Set
// as the library-wide default so every instance picks it up — the app's own
// OverlayScroll regions and the generic-components tables (StyledTableScroll
// initializes its own instance and passes no options) alike. The library
// default is 'never', which kept a grey track permanently visible on every
// scroll region; its 1300ms hide delay lingers, so the bar goes away sooner.
// On pointer devices GlobalStyles additionally gates the bar on the host's
// :hover, because this mode also reveals it on programmatic scrolls.
OverlayScrollbars.env().setDefaultOptions({
  scrollbars: { autoHide: "leave", autoHideDelay: 500 },
});

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </React.StrictMode>,
);
